import { computed, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import type { Activity, ActivityCategory, ActivitySeverity, AllActivitiesRow, PartnerActivityRow } from "@/types";
import type { ApiError } from "@/helpers/api-error";

const BASE = `${import.meta.env.VITE_API_URL}/activities`;
const PARTNER_BASE = `${import.meta.env.VITE_API_URL}/partners/activities`;
const ADMIN_BASE = `${import.meta.env.VITE_API_URL}/admin/activities`;

export interface UseActivitiesOptions {
  /** Restrict to specific categories — repeated as `?category=Product&category=Subscription`. */
  categories?: Ref<ActivityCategory[] | undefined> | ActivityCategory[];
  /** Page size. Defaults to 20 server-side. */
  take?: Ref<number | undefined> | number;
  /** Restrict to activities about a specific aggregate type (e.g. `"Product"`, `"Workflow"`). */
  subjectType?: Ref<string | undefined> | string;
  /** Restrict to activities about a specific aggregate id. */
  subjectId?: Ref<string | undefined> | string;
  /** Restrict to activities tagged with a specific project id (project-history feeds). */
  projectId?: Ref<string | undefined> | string;
  /** Restrict to activities triggered by a specific user (profile timeline). */
  actorUserId?: Ref<string | undefined> | string;
  /** Disable polling entirely; pass `false` to opt out. Defaults to refetch on window focus. */
  enabled?: Ref<boolean> | boolean;
}

/**
 * Fetch the current org's recent activities. Server scopes by the authenticated user's OrgId —
 * the SPA never passes one. Each surface (Publisher dashboard, Org overview) chooses its own
 * category filter.
 */
export function useActivities(options: UseActivitiesOptions = {}) {
  const categories = computed(() =>
    Array.isArray(options.categories) ? options.categories : options.categories?.value,
  );
  const take = computed(() =>
    typeof options.take === "number" ? options.take : options.take?.value,
  );
  const subjectType = computed(() =>
    typeof options.subjectType === "string" ? options.subjectType : options.subjectType?.value,
  );
  const subjectId = computed(() =>
    typeof options.subjectId === "string" ? options.subjectId : options.subjectId?.value,
  );
  const projectId = computed(() =>
    typeof options.projectId === "string" ? options.projectId : options.projectId?.value,
  );
  const actorUserId = computed(() =>
    typeof options.actorUserId === "string" ? options.actorUserId : options.actorUserId?.value,
  );

  const url = computed(() => {
    const params = new URLSearchParams();
    for (const c of categories.value ?? []) params.append("category", c);
    if (take.value !== undefined) params.set("take", String(take.value));
    if (subjectType.value) params.set("subjectType", subjectType.value);
    if (subjectId.value) params.set("subjectId", subjectId.value);
    if (projectId.value) params.set("projectId", projectId.value);
    if (actorUserId.value) params.set("actorUserId", actorUserId.value);
    const qs = params.toString();
    return qs ? `${BASE}?${qs}` : BASE;
  });

  const { data, isFetching, error, refetch } = useQuery({
    queryKey: ["activities", categories, take, subjectType, subjectId, projectId, actorUserId],
    queryFn: () => fetchWrapper.get<Activity[]>(url.value),
    enabled: typeof options.enabled === "boolean"
      ? options.enabled
      : (options.enabled ?? true),
  });

  const activities = computed(() => data.value ?? []);

  return {
    activities,
    activitiesLoading: isFetching,
    activitiesError: error as Ref<ApiError | null>,
    refetch,
  };
}

/**
 * Renders an activity's `titleTemplate` against its `tokens`. Replaces `{key}` placeholders
 * with the corresponding token value; `{actor}` and `{subject}` use the activity's display
 * names by convention.
 */
export function renderActivityTitle(activity: Activity): string {
  return activity.titleTemplate.replace(/\{([^}]+)\}/g, (_, key) => {
    if (key === "actor") return activity.actorDisplayName ?? "Someone";
    if (key === "subject") return activity.subjectDisplayName;
    return activity.tokens[key] ?? `{${key}}`;
  });
}

/**
 * "5 minutes ago" / "2 hours ago" / "Just now" rendering for activity timestamps. Pure —
 * call from a `computed` or template-side helper.
 */
export function formatActivityTimestamp(occurredOn: string): string {
  const seconds = Math.floor((Date.now() - new Date(occurredOn).getTime()) / 1000);
  if (seconds < 60) return "Just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

/**
 * Bootstrap icon class for an activity, picked by category + severity. Errors and warnings
 * win over the category-default icon so they read at a glance.
 */
export function activityIconClass(activity: Activity): string {
  if (activity.severity === "Error") return "bi bi-exclamation-triangle";
  if (activity.severity === "Warning") return "bi bi-exclamation-circle";

  switch (activity.category) {
    case "Workflow": return "bi bi-diagram-3";
    case "Product": return "bi bi-grid";
    case "Subscription": return "bi bi-person-plus";
    case "Project": return "bi bi-boxes";
    case "Org": return "bi bi-building";
    case "User": return "bi bi-person";
    case "WorkflowRun": return "bi bi-play-circle";
    case "Connection": return "bi bi-link";
    default: return "bi bi-activity";
  }
}

/** Bootstrap text-color class matching the activity's severity. */
export function activityColorClass(severity: Activity["severity"]): string {
  switch (severity) {
    case "Success": return "text-success";
    case "Warning": return "text-warning";
    case "Error": return "text-danger";
    case "Info":
    default: return "text-primary";
  }
}

/** Bootstrap `bg-*` badge class matching the activity's severity (filled badges). */
export function activitySeverityBadgeClass(severity: Activity["severity"]): string {
  switch (severity) {
    case "Success": return "bg-success";
    case "Error": return "bg-danger";
    case "Warning": return "bg-warning";
    case "Info":
    default: return "bg-info";
  }
}

/** Category options for activity filter dropdowns (Partner + Admin activity pages). */
export const ACTIVITY_CATEGORY_OPTIONS: { value: ActivityCategory; label: string }[] = [
  { value: "Workflow", label: "Workflow" },
  { value: "Project", label: "Project" },
  { value: "Product", label: "Product" },
  { value: "Subscription", label: "Subscription" },
  { value: "Org", label: "Organisation" },
  { value: "User", label: "User" },
  { value: "WorkflowRun", label: "Workflow Run" },
  { value: "Connection", label: "Connection" },
];

/** Severity options for activity filter dropdowns (Partner + Admin activity pages). */
export const ACTIVITY_SEVERITY_OPTIONS: { value: ActivitySeverity; label: string }[] = [
  { value: "Success", label: "Success" },
  { value: "Info", label: "Info" },
  { value: "Warning", label: "Warning" },
  { value: "Error", label: "Error" },
];

// =====================================================================
// Partner activities — fans-in across the partner's client orgs.
// =====================================================================

export interface UsePartnerActivitiesOptions {
  clientOrgId?: Ref<string | undefined> | string;
  projectId?: Ref<string | undefined> | string;
  categories?: Ref<ActivityCategory[] | undefined> | ActivityCategory[];
  severities?: Ref<ActivitySeverity[] | undefined> | ActivitySeverity[];
  take?: Ref<number | undefined> | number;
  enabled?: Ref<boolean> | boolean;
}

function unwrap<T>(value: Ref<T> | T | undefined): T | undefined {
  if (value === undefined) return undefined;
  if (typeof value === "object" && value !== null && "value" in (value as object)) {
    return (value as Ref<T>).value;
  }
  return value as T;
}

/**
 * Fetch partner activities — server scopes to the caller's active client orgs via
 * <c>OrgPartnership</c>. Pass <c>clientOrgId</c> to narrow to a single client.
 */
export function usePartnerActivities(options: UsePartnerActivitiesOptions = {}) {
  const url = computed(() => {
    const params = new URLSearchParams();
    const clientOrgId = unwrap(options.clientOrgId);
    const projectId = unwrap(options.projectId);
    const categories = unwrap(options.categories);
    const severities = unwrap(options.severities);
    const take = unwrap(options.take);

    if (clientOrgId) params.set("clientOrgId", clientOrgId);
    if (projectId) params.set("projectId", projectId);
    for (const c of categories ?? []) params.append("category", c);
    for (const s of severities ?? []) params.append("severity", s);
    if (take !== undefined) params.set("take", String(take));

    const qs = params.toString();
    return qs ? `${PARTNER_BASE}?${qs}` : PARTNER_BASE;
  });

  const { data, isFetching, error, refetch } = useQuery({
    queryKey: ["partner-activities", url],
    queryFn: () => fetchWrapper.get<PartnerActivityRow[]>(url.value),
    enabled: typeof options.enabled === "boolean"
      ? options.enabled
      : (options.enabled ?? true),
  });

  const activities = computed(() => data.value ?? []);

  return {
    activities,
    activitiesLoading: isFetching,
    activitiesError: error as Ref<ApiError | null>,
    refetch,
  };
}

// =====================================================================
// SuperAdmin god-view — cross-partition across every org's activities.
// =====================================================================

export interface UseAllActivitiesOptions {
  orgId?: Ref<string | undefined> | string;
  categories?: Ref<ActivityCategory[] | undefined> | ActivityCategory[];
  severities?: Ref<ActivitySeverity[] | undefined> | ActivitySeverity[];
  actorUserId?: Ref<string | undefined> | string;
  subjectType?: Ref<string | undefined> | string;
  subjectId?: Ref<string | undefined> | string;
  projectId?: Ref<string | undefined> | string;
  before?: Ref<string | undefined> | string;
  take?: Ref<number | undefined> | number;
  enabled?: Ref<boolean> | boolean;
}

/**
 * SuperAdmin god-view across every org's activities. Cross-partition Cosmos query unless
 * <c>orgId</c> is supplied to narrow. The server enforces the SuperAdmin policy on the
 * endpoint — a 403 will come back if a non-admin reaches this composable.
 */
export function useAllActivities(options: UseAllActivitiesOptions = {}) {
  const orgId = computed(() => unwrap(options.orgId));
  const categories = computed(() =>
    Array.isArray(options.categories) ? options.categories : options.categories?.value,
  );
  const severities = computed(() =>
    Array.isArray(options.severities) ? options.severities : options.severities?.value,
  );
  const actorUserId = computed(() => unwrap(options.actorUserId));
  const subjectType = computed(() => unwrap(options.subjectType));
  const subjectId = computed(() => unwrap(options.subjectId));
  const projectId = computed(() => unwrap(options.projectId));
  const before = computed(() => unwrap(options.before));
  const take = computed(() => unwrap(options.take));

  const url = computed(() => {
    const params = new URLSearchParams();
    if (orgId.value) params.set("orgId", orgId.value);
    for (const c of categories.value ?? []) params.append("category", c);
    for (const s of severities.value ?? []) params.append("severity", s);
    if (actorUserId.value) params.set("actorUserId", actorUserId.value);
    if (subjectType.value) params.set("subjectType", subjectType.value);
    if (subjectId.value) params.set("subjectId", subjectId.value);
    if (projectId.value) params.set("projectId", projectId.value);
    if (before.value) params.set("before", before.value);
    if (take.value !== undefined) params.set("take", String(take.value));
    const qs = params.toString();
    return qs ? `${ADMIN_BASE}?${qs}` : ADMIN_BASE;
  });

  const { data, isFetching, error, refetch } = useQuery({
    queryKey: ["admin-activities", url],
    queryFn: () => fetchWrapper.get<AllActivitiesRow[]>(url.value),
    enabled: typeof options.enabled === "boolean"
      ? options.enabled
      : (options.enabled ?? true),
  });

  const activities = computed(() => data.value ?? []);

  return {
    activities,
    activitiesLoading: isFetching,
    activitiesError: error as Ref<ApiError | null>,
    refetch,
  };
}

/**
 * Count of partner-client activities since the given ISO timestamp. Defaults to last 24h —
 * matches the "recent activities" stat card on the partner dashboard.
 */
export function usePartnerActivityCount(since?: Ref<string> | string) {
  const sinceIso = computed(() => {
    if (typeof since === "string") return since;
    if (since?.value) return since.value;
    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000);
    return yesterday.toISOString();
  });

  const { data, isFetching, error } = useQuery({
    queryKey: ["partner-activity-count", sinceIso],
    queryFn: () => fetchWrapper.get<number>(
      `${PARTNER_BASE}/count?since=${encodeURIComponent(sinceIso.value)}`,
    ),
  });

  const count = computed(() => data.value ?? 0);
  return { count, countLoading: isFetching, countError: error as Ref<ApiError | null> };
}
